
export enum UserRole {
  USER = 'User',
  ADMIN = 'Admin',
  TEAM_MEMBER = 'Team Member',
  AGENCY_CLIENT = 'Agency Client'
}

export enum PlanType {
  FREE = 'Free',
  PRO = 'Pro',
  PRO_PLUS = 'Pro+'
}

export enum AppearanceMode {
  LIGHT = 'light',
  DARK = 'dark',
  AUTO = 'auto'
}

// --- Data Pipeline Types ---

export type FieldType = 'string' | 'number' | 'boolean' | 'date' | 'url' | 'email' | 'array' | 'object';

export interface FieldCatalogEntry {
  field_key: string;
  label: string;
  type: FieldType;
  example_value?: any;
  sensitivity?: 'public' | 'internal' | 'pii';
  required?: boolean;
}

export interface Transformation {
  id: string;
  type: 'rename' | 'format' | 'merge' | 'split' | 'default' | 'filter' | 'dedupe';
  config: any;
}

export interface InputBinding {
  to_input_key: string;
  source_type: 'field' | 'literal' | 'template';
  source_value: string;
}

// --- Orchestration Model ---

export type TriggerType = 'manual' | 'schedule' | 'webhook' | 'bot_message' | 'file_upload' | 'condition';

export type InputSourceType = 'preset' | 'dataset_query' | 'webhook_payload' | 'bot_payload' | 'file_payload';

export type IntentType =
  | 'structured_dataset'
  | 'enriched_dataset'
  | 'outreach_drafts'
  | 'summary_analysis'
  | 'notifications'
  | 'file_export'
  | 'dataset_update'
  | 'social_post'
  | 'api_output'
  | 'bot_response';

export type OutputMode = 'json' | 'csv' | 'pdf' | 'html' | 'text' | 'webhook' | 'email' | 'social_post';

export interface FileMetadata {
  name: string;
  size: number;
  type: string;
  rowCount: number;
  columns: string[];
  preview: any[];
}

export interface InputSource {
  type: InputSourceType;
  config: any;
  fileMetadata?: FileMetadata;
  selectedFields: string[];
}

export interface StepMapping {
  transformations: Transformation[];
  input_bindings: InputBinding[];
}

export interface AutomationStep {
  id: string;
  toolId: string;
  mapping: StepMapping;
}

export interface Automation {
  id: string;
  name: string;
  description: string;
  status: 'enabled' | 'paused' | 'draft';
  trigger: { type: TriggerType; config?: any };
  inputSource: InputSource; // Step 0
  intent: IntentType; // Constraint
  steps: AutomationStep[]; // Tools 1...N
  finalOutputMode?: OutputMode; // Derived from last tool
  run_policy: {
    priority: 'low' | 'normal' | 'high';
    busy_policy: 'queue' | 'skip' | 'reschedule';
    max_runs_per_day: number;
  };
  createdAt: number;
}

// --- AI Tool & Run Types ---

export interface Tool {
  id: string;
  catalogId?: string;
  commandSlug?: string;
  resultEngine?: string;
  resultTemplateId?: string;
  resultRenderMap?: Record<string, any> | null;
  name: string;
  category: string | string[];
  subCategory: string | string[];
  description: string;
  longDescription?: string;
  creditCost: number;
  status: string;
  badges: string[];
  icon: string;
  form_structure_html?: string;
  responseModes?: string[];
  inputSchema: FieldCatalogEntry[];
  inputSpec?: Record<string, any> | null;
  outputSpec?: Record<string, any> | null;
  supportedOutputModes: OutputMode[];
  creditRules?: string;
  visibility?: string;
  minPlanRequired?: string;
  statistics?: {
    activations: number;
    totalExecutions: number;
  };
}

export interface RenderBlock {
  id: string;
  type: string;
  payload: any;
}

export interface ResultTemplate {
  template_id: string;
  template_name: string;
  template_slug: string;
  engine_type: string;
  supported_result_types: string[];
  required_blocks_json?: string[];
  optional_blocks_json?: string[];
  input_schema_hint_json: Record<string, any>;
  output_schema_hint_json: Record<string, any>;
  render_contract_json?: Record<string, any>;
  render_map_json?: Record<string, any>;
  css_hooks_json: Record<string, any> | string[];
  conversion_hooks_json?: Record<string, any>;
  render_notes?: string;
  status: string;
  html_template?: string;
  html_file?: string;
}

export interface ResultEnvelope {
  run_id: string;
  status: 'success' | 'error' | 'partial';
  meta?: {
      tool_id?: string;
      result_engine?: string;
      result_template_id?: string;
      [key: string]: any;
  };
  data?: any;
  outputSpec?: any;
  output_spec_json?: any;
  renderMap?: any;
  render_map?: any;
  render_blocks?: RenderBlock[];
  raw?: string;
  downloads?: any[];
}

export interface ToolRun {
  id: string;
  toolId: string;
  timestamp: number;
  creditsSpent: number;
  status: string;
  summaryText: string;
  htmlContent: string;
  normalizedOutput?: any;
  renderBlocks?: RenderBlock[];
  responseEnvelope?: ResultEnvelope;
}

export interface MetersSnapshot {
  credit_balance: number;
  credits_reserved: number;
  bot_runs_hour: number;
  bot_runs_day: number;
  bot_capacity_total: number;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  plan: PlanType;
  creditsRemaining: number;
  activeToolIds: string[];
  appearanceMode: AppearanceMode;
  storageUsedGb: number;
  pinnedToolIds: string[];
}
